import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  Package,
  Key,
  CreditCard,
  LogOut,
  User,
  ExternalLink,
  Compass,
  ArrowRight,
  Ticket,
  Sparkles,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  MessageCircle,
  Download,
  AlertTriangle,
  Gift,
  Copy,
  Share2,
} from "lucide-react";
import Button from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { CARTOMANCER_LOGO, isCartomancerProduct, cartomancerLogoFor } from "../branding";
import LanguageSelector from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;

const UserDashboard = () => {
  const { t } = useTranslation();
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [premium, setPremium] = useState({ active: false });
  const [trialEligibility, setTrialEligibility] = useState({ eligible: false });
  const [claimingTrial, setClaimingTrial] = useState(false);
  const [trialResult, setTrialResult] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copiedInvite, setCopiedInvite] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) fetchUserData();
  }, [user]);

  const fetchUserData = async () => {
    try {
      const [ordersRes, licensesRes, productsRes, trialRes, refRes] = await Promise.all([
        fetch(`${API}/api/users/${user.user_id}/orders`, { credentials: "include" }),
        fetch(`${API}/api/users/me/licenses`, { credentials: "include" }),
        fetch(`${API}/api/licenses/my-products`, { credentials: "include" }),
        fetch(`${API}/api/users/me/trial-eligibility`, { credentials: "include" }),
        fetch(`${API}/api/users/me/referral`, { credentials: "include" }),
      ]);

      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (licensesRes.ok) {
        const d = await licensesRes.json();
        setLicenses(d.licenses || []);
      }
      if (productsRes.ok) {
        const d = await productsRes.json();
        setMyProducts(d.products || []);
        setPremium(d.premium || { active: false });
      }
      if (trialRes.ok) setTrialEligibility(await trialRes.json());
      if (refRes.ok) setReferral(await refRes.json());
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const copyInviteUrl = async () => {
    if (!referral?.invite_url) return;
    try {
      await navigator.clipboard.writeText(referral.invite_url);
      setCopiedInvite(true);
      setTimeout(() => setCopiedInvite(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  const handleClaimTrial = async () => {
    setClaimingTrial(true);
    setTrialResult(null);
    try {
      const r = await fetch(`${API}/api/users/me/claim-premium-trial`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        setTrialResult({
          success: true,
          code: d.trial_code,
          ticket_id: d.ticket_id,
          duration: d.trial_duration_days,
          code_expires_at: d.code_expires_at,
        });
        fetchUserData();
      } else {
        const err = await r.json();
        const msg = err.detail?.message || err.detail || "Impossibile richiedere il Trial";
        setTrialResult({ success: false, message: msg });
      }
    } catch (e) {
      setTrialResult({ success: false, message: "Errore di rete" });
    } finally {
      setClaimingTrial(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-16 h-16 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-4xl text-gold mb-1">Dashboard</h1>
            <p className="text-muted-foreground">Benvenuto, {user.name}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/redeem">
              <Button variant="gold" size="sm" data-testid="dash-redeem-cta">
                <Ticket className="w-4 h-4 mr-2" />
                Riscatta Codice
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="ghost" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>

        {/* PROFILE */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 flex-wrap">
              {user.picture && (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-heading text-lg text-foreground">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {user.role === "admin" && (
                    <span className="px-2 py-1 bg-ruby/20 border border-ruby text-ruby text-m rounded font-heading">
                      ADMIN
                    </span>
                  )}
                  {premium.active && (
                    <span
                      className="px-2 py-1 bg-arcane/20 border border-arcane text-arcane text-m rounded font-heading"
                      data-testid="user-premium-badge"
                    >
                      {premium.permanent
                        ? "PREMIUM ATTIVO"
                        : `PREMIUM · scade il ${new Date(premium.expires_at).toLocaleDateString("it-IT")}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PREMIUM EXPIRING SOON BANNER — shown when premium expires within 2 days */}
        {(() => {
          if (!premium.active || premium.permanent || !premium.expires_at) return null;
          const exp = new Date(premium.expires_at);
          const now = new Date();
          const msLeft = exp - now;
          const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
          if (msLeft <= 0 || daysLeft > 2) return null;
          const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
          const label = daysLeft >= 1 ? `${daysLeft} giorno${daysLeft > 1 ? "i" : ""}` : `${hoursLeft} ore`;
          return (
            <div
              className="mb-8 rune-border bg-gradient-to-br from-ruby/20 to-gold/10 border border-ruby/50 p-5 rounded-xl"
              data-testid="premium-expiring-banner"
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-full bg-ruby/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-ruby" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <p className="font-heading text-lg text-ruby mb-1">
                    Il tuo Premium sta per scadere
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Rimangono solo{" "}
                    <span className="text-gold font-heading" data-testid="premium-time-left">{label}</span>{" "}
                    prima della scadenza ({exp.toLocaleDateString("it-IT")}). Acquista Premium per non
                    perdere l'accesso alle funzionalità avanzate di Cartomancer.
                  </p>
                </div>
                <Link to="/store">
                  <Button variant="primary" size="lg" className="transition-all" data-testid="premium-renew-cta">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Acquista Premium
                  </Button>
                </Link>
              </div>
            </div>
          );
        })()}

        {/* PREMIUM TRIAL BANNER — shown ONLY if eligible */}
        {trialEligibility.eligible && !trialResult?.success && (
          <div
            className="mb-8 rune-border bg-gradient-to-br from-arcane/20 via-purple-900/20 to-gold/10 p-6 rounded-xl relative overflow-hidden"
            data-testid="premium-trial-banner"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-full bg-arcane/30 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-7 h-7 text-arcane" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <p className="font-heading text-xl text-gold mb-1">
                  Riscatta 7 giorni gratuiti di Premium
                </p>
                <p className="text-sm text-muted-foreground">
                  Sblocca funzionalità Premium per una settimana. Riceverai un codice via ticket
                  che potrai riscattare quando vuoi.
                </p>
                {trialResult?.success === false && (
                  <p className="text-xs text-ruby mt-2" data-testid="trial-error">
                    ❌ {trialResult.message}
                  </p>
                )}
              </div>
              <Button
                onClick={handleClaimTrial}
                disabled={claimingTrial}
                variant="arcane"
                size="lg"
                data-testid="premium-trial-claim-btn"
              >
                {claimingTrial ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Richiesta...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Richiedi Trial Premium</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* TRIAL SUCCESS RECEIPT */}
        {trialResult?.success && (
          <div
            className="mb-8 rune-border bg-gold/10 border border-gold p-6 rounded-xl"
            data-testid="trial-success-box"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <CheckCircle2 className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-[220px]">
                <p className="font-heading text-lg text-gold mb-2">
                  Codice Trial Premium generato!
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  Abbiamo creato un ticket con il tuo codice. Aprilo per riscattare i {trialResult.duration} giorni di Premium.
                </p>
                <div className="p-3 bg-background/60 rounded-lg mb-3">
                  <p className="text-xs text-muted-foreground mb-1">Codice:</p>
                  <p className="font-mono text-lg text-gold tracking-wider" data-testid="trial-code-display">
                    {trialResult.code}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link to="/contact">
                    <Button variant="primary" size="sm" data-testid="trial-open-ticket">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Vai al Ticket
                    </Button>
                  </Link>
                  <Link to="/redeem">
                    <Button variant="gold" size="sm">
                      <Ticket className="w-4 h-4 mr-2" />
                      Riscatta Ora
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION: LE MIE WEBAPP */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Compass className="w-5 h-5 mr-2 text-gold" />
              Le Mie WebApp
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myProducts.length === 0 ? (
              <div className="text-center py-8">
                <Compass className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Nessuna webapp disponibile</p>
                <Link to="/store">
                  <Button variant="primary" size="sm">Vai allo Store</Button>
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {myProducts.map((product) => {
                  const isTemp = product.access_type === "temporary" && product.expires_at;
                  const expiryDate = isTemp ? new Date(product.expires_at) : null;
                  const displayTier = product.premium_overlay
                    ? "Premium (Codice)"
                    : product.tier_label || (product.subscription ? "Premium" : "Basic");
                  return (
                    <div
                      key={product.product_id}
                      className="rune-border bg-gradient-to-br from-gold/5 to-arcane/5 rounded-lg p-5"
                      data-testid={`my-product-${product.product_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-gold/10 rounded-lg flex items-center justify-center overflow-hidden">
                            {isCartomancerProduct(product.product_id) ? (
                              <img
                                src={cartomancerLogoFor(product.product_id)}
                                alt={product.name}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Compass className="w-6 h-6 text-gold" />
                            )}
                          </div>
                          <div>
                            <p className="font-heading text-lg text-foreground" data-testid={`product-name-${product.product_id}`}>
                              {product.name}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid={`product-tier-${product.product_id}`}>
                              {displayTier}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-3 py-1 border text-xs rounded font-heading ${
                            isTemp
                              ? "bg-arcane/20 border-arcane text-arcane"
                              : "bg-gold/20 border-gold text-gold"
                          }`}
                          data-testid={`product-status-${product.product_id}`}
                        >
                          {isTemp ? "TEMPORANEA" : "ATTIVA"}
                        </span>
                      </div>

                      {isTemp && (
                        <p className="text-xs text-muted-foreground mb-3 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Scade il {expiryDate.toLocaleDateString("it-IT")}
                        </p>
                      )}

                      <Link to={product.access_url || "/app"}>
                        <Button variant="primary" size="sm" className="w-full" data-testid={`open-webapp-${product.product_id}`}>
                          Apri WebApp
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION: LE MIE LICENZE */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center">
                <Key className="w-5 h-5 mr-2" />
                Le Mie Licenze
              </CardTitle>
              {licenses.length > 0 && (
                <a
                  href={`${API}/api/users/me/licenses/export.csv`}
                  download
                  data-testid="licenses-export-csv"
                >
                  <Button variant="ghost" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Esporta CSV
                  </Button>
                </a>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {licenses.length === 0 ? (
              <div className="text-center py-8">
                <Key className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna licenza attiva</p>
              </div>
            ) : (
              <div className="space-y-3">
                {licenses.map((license) => (
                  <div
                    key={license.license_id}
                    className={`p-4 border rounded-lg ${
                      license.status === "expired"
                        ? "border-border/50 opacity-70"
                        : "border-border hover:border-gold transition-colors"
                    }`}
                    data-testid={`license-${license.license_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-[220px]">
                        <p className="font-heading text-foreground">{license.product_name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {license.license_type === "permanent" ? "Licenza permanente" : "Licenza temporanea"}
                          {license.source === "code" && " · da codice"}
                          {license.source === "admin" && " · concessa da admin"}
                          {license.source === "purchase" && " · acquistata"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Attivata il{" "}
                          {license.activated_at
                            ? new Date(license.activated_at).toLocaleDateString("it-IT")
                            : "—"}
                          {license.expires_at && (
                            <> · Scade il {new Date(license.expires_at).toLocaleDateString("it-IT")}</>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        {license.status === "active" ? (
                          license.license_type === "permanent" ? (
                            <span
                              className="px-3 py-1 bg-gold/20 border border-gold text-gold text-xs rounded font-heading"
                              data-testid={`license-status-${license.license_id}`}
                            >
                              PERMANENTE
                            </span>
                          ) : (
                            <span
                              className="px-3 py-1 bg-arcane/20 border border-arcane text-arcane text-xs rounded font-heading"
                              data-testid={`license-status-${license.license_id}`}
                            >
                              {license.days_remaining === 0
                                ? "Ultimo giorno"
                                : license.days_remaining === 1
                                ? "1 giorno rimanente"
                                : `${license.days_remaining} giorni rimanenti`}
                            </span>
                          )
                        ) : (
                          <span
                            className="px-3 py-1 bg-muted/20 border border-border text-muted-foreground text-xs rounded font-heading"
                            data-testid={`license-status-${license.license_id}`}
                          >
                            SCADUTA
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION: I MIEI ORDINI (pure history — no license buttons) */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Package className="w-5 h-5 mr-2" />
              I Miei Ordini
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Nessun acquisto</p>
                <Link to="/store">
                  <Button variant="primary" size="sm">Vai allo Store</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-4">N° Ordine</th>
                      <th className="py-2 pr-4">Data</th>
                      <th className="py-2 pr-4">Prodotto</th>
                      <th className="py-2 pr-4">Importo</th>
                      <th className="py-2 pr-4">Metodo</th>
                      <th className="py-2 pr-4">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr
                        key={o.order_id}
                        className="border-b border-border/40"
                        data-testid={`order-${o.order_id}`}
                      >
                        <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
                          {o.order_id.slice(0, 12)}…
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString("it-IT") : "—"}
                        </td>
                        <td className="py-3 pr-4 font-heading text-foreground">
                          {o.product_name || o.product_id}
                        </td>
                        <td className="py-3 pr-4 text-foreground">
                          €{Number(o.amount || 0).toFixed(2)}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground capitalize">
                          {o.payment_method || "—"}
                        </td>
                        <td className="py-3 pr-4">
                          {o.payment_status === "paid" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold/20 text-gold text-xs rounded font-heading">
                              <CheckCircle2 className="w-3 h-3" /> Pagato
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted/20 text-muted-foreground text-xs rounded font-heading">
                              <XCircle className="w-3 h-3" /> {o.payment_status || "In attesa"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-8">
          {/* PROGRAMMA REFERRAL */}
          {referral && (
            <Card className="border-gold/40 md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-gold" />
                  Invita Amici — Guadagna Premium
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Condividi il tuo link. Quando un amico si iscrive tramite il tuo codice e fa il suo primo acquisto,
                  ricevi <span className="text-gold font-heading">15 giorni Premium gratis</span>.
                </p>

                <div className="grid sm:grid-cols-3 gap-3 text-center">
                  <div className="p-3 rounded-lg bg-background/60 border border-border">
                    <p className="text-xs uppercase text-muted-foreground">Invitati</p>
                    <p className="font-heading text-2xl text-arcane" data-testid="referral-invited-count">{referral.invited_count}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background/60 border border-border">
                    <p className="text-xs uppercase text-muted-foreground">Convertiti</p>
                    <p className="font-heading text-2xl text-gold" data-testid="referral-converted-count">{referral.converted_count}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background/60 border border-border">
                    <p className="text-xs uppercase text-muted-foreground">Giorni Premium</p>
                    <p className="font-heading text-2xl text-gold" data-testid="referral-days-earned">{referral.premium_days_earned}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Il tuo link di invito:</p>
                  <div className="flex gap-2 items-center flex-wrap">
                    <code
                      className="flex-1 min-w-[220px] p-2 rounded bg-background/60 border border-border font-mono text-xs text-gold overflow-x-auto whitespace-nowrap"
                      data-testid="referral-invite-url"
                    >
                      {referral.invite_url}
                    </code>
                    <Button onClick={copyInviteUrl} variant="gold" size="sm" data-testid="referral-copy-btn">
                      {copiedInvite ? (
                        <><CheckCircle2 className="w-4 h-4 mr-2" />Copiato!</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" />Copia</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Il tuo codice: <span className="font-mono text-gold" data-testid="referral-code">{referral.referral_code}</span>
                  </p>
                </div>

                {referral.recent_invited.length > 0 && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-2">Amici recenti:</p>
                    <div className="space-y-1">
                      {referral.recent_invited.slice(0, 5).map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-xs border-b border-border/40 py-1">
                          <span className="text-muted-foreground">{f.name || f.email_mask}</span>
                          {f.converted ? (
                            <span className="px-2 py-0.5 bg-gold/20 text-gold rounded font-heading">CONVERTITO</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-muted/20 text-muted-foreground rounded font-heading">IN ATTESA</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

{/* FATTURAZIONE */}
<Card> <CardHeader> <CardTitle className="flex items-center"> <CreditCard className="w-5 h-5 mr-2" />
Fatturazione </CardTitle> </CardHeader>

  <CardContent>
    <p className="text-muted-foreground text-sm mb-4">
      Gestisci i tuoi metodi di pagamento e abbonamenti.
    </p>

<Button
  variant="secondary"
  size="sm"
  className="w-full mb-3"
  data-testid="dash-open-billing-portal"
  onClick={async () => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-portal-session-me`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            return_url: window.location.href,
          }),
        }
      );

      const data = await resp.json();

      if (resp.ok && data?.url) {
        window.location.href = data.url;
      } else {
        alert(
          data?.detail ||
            "Impossibile aprire il Customer Portal."
        );
      }
    } catch (e) {
      alert("Errore di rete nell'apertura del portale.");
    }
  }}
>
  <ExternalLink className="w-4 h-4 mr-2" />
  Apri Stripe Portal
</Button>

<Button
  variant="secondary"
  size="sm"
  className="w-full mb-3"
  onClick={() => {
    window.open(
      "https://www.paypal.com/myaccount/autopay/",
      "_blank",
      "noopener,noreferrer"
    );
  }}
>
  <ExternalLink className="w-4 h-4 mr-2" />
  Apri PayPal
</Button>

  </CardContent>
</Card>




          {/* LINGUA / LANGUAGE */}
          <Card data-testid="dash-language-card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <span className="mr-2 text-gold">🌐</span>
                {t("language.label")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LanguageSelector variant="panel" />
            </CardContent>
          </Card>

          {/* MESSAGGI / TICKET */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageCircle className="w-5 h-5 mr-2 text-gold" />
                Messaggi / Ticket
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm mb-4">
                Visualizza le tue conversazioni con il supporto Mytharion.
              </p>
              <Link to="/contact">
                <Button variant="primary" size="sm" className="w-full" data-testid="dash-open-tickets">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Vai ai Miei Ticket
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* ADMIN LINK */}
          {user.role === "admin" && (
            <Card className="border-ruby/50 md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center text-ruby">
                  <User className="w-5 h-5 mr-2" />
                  Admin Panel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Accedi al pannello amministratore per gestire prodotti, ordini e utenti.
                </p>
                <Link to="/admin">
                  <Button variant="destructive" size="sm" className="w-full">
                    Apri Admin Panel
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
